
import { Injectable, NgZone } from "@angular/core";
import {
  Auth,
  authState,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  User as FirebaseUser,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  linkWithCredential,
  onAuthStateChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  useDeviceLanguage
} from '@angular/fire/auth';
import { Observable } from "rxjs";

import { UserModel, UserModelRegStatus } from 'src/app/shared-lib/domains/user';

import { HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";

// TODO SE DEBE REALIZAR LA CONFIGURACIÓN PARA HACER LOGIN CON FACEBOOK IGUAL QUE CON GOOGLE

export enum AuthProviders {
  Github = 0,
  Twitter = 1,
  Facebook = 2,
  Google = 3,
  Password = 4,
  Anonymous = 5,
  Custom = 6,
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  public authState$: Observable<FirebaseUser | null>;
  public authState: FirebaseUser | null;
  public loggingOut: boolean = false;
  constructor(
    public afAuth: Auth,
    private ngZone: NgZone
  ) {

    useDeviceLanguage(this.afAuth);
    this.authState$ = authState(this.afAuth);
    this.authState$.subscribe((user: FirebaseUser | null) => {
      this.authState = user;
    });
    //Esto seria para actualizar user
    onAuthStateChanged(this.afAuth, (user) => {
      if (user) {
        if (user.isAnonymous === true) {
          // Redirect to a page for the user to give you their credentials.
        } else {
          // console.log("AuthService: Cambio de User", user)
          // Give access to the detail page.
        }
      }
    });

  }

  // Returns true if user is logged in
  get authenticated(): boolean {
    return !!this.authState;
  }

  // Returns current user //deberia ser current auth
  get currentUser$(): any {
    return this.authState$;
  }

  async getIdToken(): Promise<string | null> {
    const user = await this.afAuth.currentUser;
    return user ? user.getIdToken() : null;
  }

  createAnonymousUser(): Promise<any> {
    this.loggingOut = true;
    return signInAnonymously(this.afAuth).then(() => {
      this.loggingOut = false;
    });
  }

  doLogout() {
    return signOut(this.afAuth);
  }

  doEmailLogin(value) {
    return new Promise<any>((resolve, reject) => {
      signInWithEmailAndPassword(this.afAuth, value.email, value.password)
        .then((res) => {
          resolve(res);
        })
        .catch((res) => {
          reject(res);
        });
    });
  }

  doLinkEmail(data) {
    const credential = EmailAuthProvider.credential(
      data.email,
      data.password
    );
    if (!this.authState) {
      return Promise.reject(new Error('No authenticated user'));
    }
    return linkWithCredential(this.authState, credential).then((uC) => {
      const user: UserModel = {} as UserModel;
      user.emailVerified = uC.user.emailVerified;
      user.regStatus = UserModelRegStatus.Location;
      user.uid = uC.user.uid;
      user.provider = {
        ["email"]: uC.user.email,
      };
      return user;
    });
  }

  doRegister(credential) {
    return createUserWithEmailAndPassword(this.afAuth, credential.email, credential.password)
      .then((uC) => {
        // Manera de instanciar el user equivalente al uso anterior del constructor de UserModel
        const user: UserModel = {} as UserModel;
        user.email = uC.user.email;
        user.emailVerified = uC.user.emailVerified;
        user.phoneNumber = uC.user.phoneNumber;
        user.phoneNumberVerified = false;
        user.regStatus = UserModelRegStatus.Location;
        user.uid = uC.user.uid;
        user.provider = {
          ["email"]: uC.user.email,
        };
        return user;
      });
  }

  doLogin(service) {
    return this.goPopupLogin(service);
  }

  mapProviderToUser(uC) {
    let isNew = uC.isNew;
    let service = uC.additionalUserInfo.providerId;

    // Manera de instanciar el user equivalente al uso anterior del constructor de UserModel
    const user: UserModel = {} as UserModel;
    user.displayName = uC.user.displayName || "";
    user.email = uC.user.email || "";
    user.emailVerified = uC.user.emailVerified || false;
    user.phoneNumber = uC.user.phoneNumber || "";
    user.phoneNumberVerified = false;
    user.photoURL = uC.user.photoURL || "";
    user.uid = uC.user.uid;
    user.regStatus = UserModelRegStatus.Name;
    user.provider = {
      [service]:
        uC.additionalUserInfo.profile["id"] ||
        uC.additionalUserInfo.profile["sub"], // TODO: esto está diferente cuando se usa el pop up
    };
    switch (service) {
      case "google.com":
        user.firstName = uC.additionalUserInfo.profile["given_name"] || "";
        user.lastName = uC.additionalUserInfo.profile["family_name"] || "";
        break;
      case "facebook.com":
        user.firstName = uC.additionalUserInfo.profile["first_name"] || "";
        user.lastName = uC.additionalUserInfo.profile["last_name"] || "";
        if (!user.email)
          user.email = uC.additionalUserInfo.profile["email"] || ""; // MN why is this?? it should come in uC.user.email
        break;
    }
    return user;
  }

  goPopupLogin(service) {
    let provider = null;
    switch (service) {
      case "google.com":
        provider = new GoogleAuthProvider();
        provider.defaultLanguageCode = "es";
        provider.addScope("profile");
        provider.addScope("email");
        break;
      case "facebook.com":
        provider = new FacebookAuthProvider();
        provider.addScope("email");
        break;

    }

    return signInWithPopup(this.afAuth, provider)
      .then((uC) => {
        const additionalInfo = getAdditionalUserInfo(uC) as any;
        const providerId = additionalInfo?.providerId;
        const profile = additionalInfo?.profile ?? {};
        const user: UserModel = {} as UserModel;
        user.displayName = uC.user.displayName || "";
        user.email = uC.user.email || "";
        user.emailVerified = uC.user.emailVerified || false;
        user.phoneNumber = uC.user.phoneNumber || "";
        user.phoneNumberVerified = false;
        user.photoURL = uC.user.photoURL || "";
        user.uid = uC.user.uid;
        user.regStatus = UserModelRegStatus.Name;
        user.provider = {
          // TODO: en el otro uso de sign up usa id || sub, aquí no?
          [providerId]:
            profile["id"],
        };
        switch (service) {
          case "google.com":
            user.firstName = profile["given_name"] || "";
            user.lastName = profile["family_name"] || "";
            if (!user.email)
              user.email = profile["email"] || ""; // MN why is this?? it should come in uC.user.email
            break;
          case "facebook.com":
            user.firstName = profile["first_name"] || "";
            user.lastName = profile["last_name"] || "";
            if (!user.email)
              user.email = profile["email"] || ""; // MN why is this?? it should come in uC.user.email
            break;
        }
        return user;
      })
      .catch((err) => {
        // MN: propagate error
        console.log("catch signInWithPopup");
        console.log(err);
      });
  }
  async _getHttpHeaders() {
    const idToken = await this.getIdToken();
    if (idToken) {
      return new HttpHeaders({
        Authorization: `Bearer ${idToken}`,
        "project-id": `${environment.firebaseConfig.projectId}`,
      });
    } else {
      return null;
    }
  }
}
